import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import GiftContextBadgeCluster from '../../../components/GiftContextBadgeCluster';
import StarRating from '../../../components/ui/StarRating';

const GiftCard = ({
  gift,
  onSave,
  onCompare,
  onRequestSimilar,
  isSaved,
  isComparing,
  wishlistLoading = false,
  isAuthenticated = false,
  // New props for enhanced functionality
  onSaveToContact,
  onMarkPurchased,
  onOpenWishlistModal,
  onRemoveFromWishlist,
  onOpenShareModal,
  onViewDetails,
  onBadgeClick,
  userState = {},
  enableGiftFeatures = true
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Legacy success rate color function (fallback)
  const getSuccessRateColor = (rate) => {
    if (rate >= 90) return 'text-success bg-success-50';
    if (rate >= 80) return 'text-warning bg-warning-50';
    return 'text-error bg-error-50';
  };

  // Map gift data to enhanced product format for new components
  const enhancedProduct = {
    // Core fields
    id: gift.id,
    name: gift.name,
    retail_price: gift.price,
    gift_score: gift.successRate ? gift.successRate / 100 : gift.gift_score, // Convert percentage to 0-1 range if needed

    // Gift context fields (map from existing or new data)
    age_range: gift.age_range || gift.ageRange,
    gender_suitability: gift.gender_suitability || gift.genderSuitability,
    occasions: gift.occasions || [],

    // Enhanced fields
    gift_description: gift.whyItWorks,
    category_details: gift.category_details || (gift.categories?.length ? {
      id: 1,
      name: gift.categories[0], // Use first category as primary
      slug: gift.categories[0]?.toLowerCase().replace(/\s+/g, '-')
    } : null),

    // Image data
    images: gift.images || (gift.image ? [{ image: gift.image, is_main: true }] : [])
  };



  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
      {/* Image Section */}
      <div className="relative overflow-hidden bg-white border-b border-gray-100" style={{ height: '240px' }}>
        <button
          onClick={() => onViewDetails && onViewDetails(gift.id)}
          className="w-full h-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset cursor-pointer relative group/image"
          aria-label={`View details for ${gift.name}`}
        >
          <Image
            src={gift.image}
            alt={gift.name}
            className={`w-full h-full object-contain transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            style={{
              padding: '8px',
              maxWidth: '100%',
              maxHeight: '100%'
            }}
            onLoad={handleImageLoad}
          />

          {/* Subtle hover overlay to indicate clickability */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover/image:bg-opacity-5 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover/image:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 text-sm font-medium text-gray-800 shadow-sm">
              <Icon name="Eye" size={16} className="inline mr-1.5" />
              View Details
            </div>
          </div>
        </button>

        {/* Overlay Actions - Share and Wishlist buttons side by side */}
        <div className="absolute top-3 right-3 flex space-x-2">
          {/* Share button - visible on hover */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent image click
              if (onOpenShareModal) {
                onOpenShareModal();
              }
            }}
            disabled={userState.isActionLoading?.share}
            className="w-8 h-8 rounded-full flex items-center justify-center shadow-soft transition-all duration-200 bg-white/90 text-gray-600 border-2 border-white/90 hover:bg-blue-500 hover:text-white hover:border-blue-500 opacity-0 group-hover:opacity-100"
            title="Share this gift"
          >
            {userState.isActionLoading?.share ? (
              <Icon name="Loader2" size={16} className="animate-spin" />
            ) : (
              // Share icon (proper share icon with nodes and connections)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            )}
          </button>

          {/* Wishlist button - always visible when saved, visible on hover when not saved */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent image click
              if (!isAuthenticated) {
                // Could show login modal or redirect to login
                return;
              }

              if (isSaved && onRemoveFromWishlist) {
                // If product is saved, remove it from wishlist
                onRemoveFromWishlist();
              } else if (!isSaved && onOpenWishlistModal) {
                // If product is not saved, open modal to add to wishlist
                onOpenWishlistModal();
              } else if (onSave) {
                // Fallback to legacy behavior if new handlers not provided
                onSave(gift.id);
              }
            }}
            disabled={userState.isActionLoading?.wishlist}
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-soft transition-all duration-200 ${isSaved
              ? 'bg-red-500 text-white border-2 border-red-500 opacity-100'
              : 'bg-white/90 text-gray-600 border-2 border-white/90 hover:bg-red-500 hover:text-white hover:border-red-500 opacity-0 group-hover:opacity-100'
              } ${userState.isActionLoading?.wishlist ? 'opacity-50 cursor-not-allowed' : ''} ${!isAuthenticated ? 'opacity-75' : ''
              }`}
            title={!isAuthenticated ? 'Login to save gifts' : isSaved ? 'Remove from wishlist' : 'Save to wishlist'}
          >
            {userState.isActionLoading?.wishlist ? (
              <Icon name="Loader2" size={16} className="animate-spin" />
            ) : isSaved ? (
              // Filled heart for saved state
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            ) : (
              // Outline heart for unsaved state
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            )}
          </button>
        </div>




      </div>

      {/* Content Section */}
      <div className="p-4">
        <div className="mb-3">
          <h3 className="font-semibold text-xl text-gray-900 mb-2 line-clamp-2 leading-tight">
            {gift.name}
          </h3>
          {gift.brand && (
            <p className="text-sm text-accent-600 mb-2">{gift.brand}</p>
          )}

          {/* Rating Section */}
          {gift.external_rating_average && (
            <StarRating
              rating={gift.external_rating_average}
              reviewCount={gift.external_rating_count}
              size="sm"
            />
          )}

          {/* Price Section */}
          <div className="mb-3">
            {gift.discount_price ? (
              <div className="flex items-center gap-2">
                <span className="text-lg text-gray-500 line-through">${gift.retail_price || gift.price}</span>
                <span className="text-xl font-bold text-red-600">${gift.discount_price}</span>
              </div>
            ) : (
              <span className="text-xl font-bold text-gray-900">${gift.retail_price || gift.price}</span>
            )}
          </div>

          {/* Enhanced Gift Context Badges */}
          {enableGiftFeatures ? (
            <GiftContextBadgeCluster
              product={enhancedProduct}
              onBadgeClick={onBadgeClick}
            />
          ) : (
            /* Legacy Category Tags */
            <div className="flex flex-wrap gap-1 mb-3">
              {gift.categories?.map((category, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-accent-100 text-accent-600 text-xs rounded-full"
                >
                  {category}
                </span>
              ))}
            </div>
          )}
        </div>





        {/* Primary View Details Button */}
        <Button
          variant="primary"
          className="w-full mb-4 py-3 text-base font-semibold"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onViewDetails && onViewDetails(gift.id)}
        >
          View Details
        </Button>

        {/* Subtle Action Buttons */}
        <div className="flex items-center justify-between text-sm text-gray-600 border-t pt-3">
          <button
            className="flex items-center py-2 px-3 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
            onClick={() => onSaveToContact && onSaveToContact()}
            disabled={userState.isActionLoading?.contact}
          >
            <Icon name="User" size={16} className="mr-2" />
            <span className="font-medium">Gift Idea</span>
          </button>

          <button
            className="flex items-center py-2 px-3 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
            onClick={() => onMarkPurchased && onMarkPurchased()}
            disabled={userState.isActionLoading?.purchase}
          >
            <Icon name="Check" size={16} className="mr-2" />
            <span className="font-medium">Mark Bought</span>
          </button>

          <button
            className="flex items-center py-2 px-3 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
            onClick={() => onRequestSimilar && onRequestSimilar(gift.id)}
          >
            <Icon name="Shuffle" size={16} className="mr-2" />
            <span className="font-medium">Similar</span>
          </button>
        </div>


      </div>
    </div>
  );
};

export default GiftCard;